import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  getCharacterRolesOptions,
  getCharacterRolesQueryKey,
  getCharacterHardsOptions,
  getCharacterHardsQueryKey
} from "@/client/@tanstack/react-query.gen";
import { getCharacters, getComboCharacters, getCharacterSynergy } from "@/client/sdk.gen";
import { readCache } from "@/store/action";
import { translate } from "@/lib";
import { useStore } from "@/store";
import type {
  GetCharacterRolesResponse,
  GetCharacterHardsResponse,
  GetCharactersResponse,
  GetComboCharactersResponse,
  GetCharacterSynergyResponse
} from "@/client/types.gen";

export type ApiCharacter = GetCharactersResponse['data'][number];
export type ApiCharacterRole = GetCharacterRolesResponse[number];
export type ApiCharacterHard = GetCharacterHardsResponse[number];
export type ApiComboEntry = GetComboCharactersResponse['data'][number];
export type ApiSynergyEntry = GetCharacterSynergyResponse['data'][number];

// Query options exports for use in App.tsx bootstrap
export const useCharacterRolesOptions = () => getCharacterRolesOptions();
export const useCharacterHardsOptions = () => getCharacterHardsOptions();

// Cache readers
export function getCharacterRoles(): GetCharacterRolesResponse | undefined {
  return readCache<ReturnType<typeof getCharacterRolesQueryKey>, GetCharacterRolesResponse>(
    getCharacterRolesQueryKey()
  );
}

export function getCharacterHards(): GetCharacterHardsResponse | undefined {
  return readCache<ReturnType<typeof getCharacterHardsQueryKey>, GetCharacterHardsResponse>(
    getCharacterHardsQueryKey()
  );
}

// Lookup helpers by ID
export function getCharacterRoleById(id: number): ApiCharacterRole | undefined {
  const roles = getCharacterRoles();
  return roles?.find(role => role.id === id);
}

export function getCharacterHardById(id: number): ApiCharacterHard | undefined {
  const hards = getCharacterHards();
  return hards?.find(hard => hard.id === id);
}

// Display name helper - use transify key to look up translation, fallback to name
function getDisplayName(item: { transify?: string; name: string } | undefined): string {
  if (!item) return '';
  return (item.transify ? translate(item.transify) : '') || item.name;
}

export function getCharacterRoleName(id: number): string {
  return getDisplayName(getCharacterRoleById(id));
}

export function getCharacterHardInfo(id: number): { name: string; color: string } {
  const hard = getCharacterHardById(id);
  return {
    name: getDisplayName(hard),
    color: hard?.color || '#95a5a6',
  };
}

async function fetchCharactersPage(cursor: number | undefined, lang: string | undefined): Promise<GetCharactersResponse> {
  const { data } = await getCharacters({
    body: { cursor, limit: 100 },
    query: { lang },
    throwOnError: true,
  });
  return data;
}

// Fetches the whole character list (looping cursor pages until exhausted).
// The dataset is small (<100 characters), so filtering/sorting is done client-side by the page.
export function useAllCharacters(enabled: boolean = true) {
  const lang = useStore((s) => s.language);

  return useQuery({
    queryKey: ['characters', 'all', lang],
    enabled,
    queryFn: async (): Promise<ApiCharacter[]> => {
      const all: ApiCharacter[] = [];
      let cursor: number | undefined = undefined;

      do {
        const page = await fetchCharactersPage(cursor, lang);
        all.push(...page.data);
        cursor = page.cursor;
      } while (cursor !== undefined);

      return all;
    },
  });
}

async function fetchCharacterCombos(characterId: number): Promise<ApiComboEntry[]> {
  const { data } = await getComboCharacters({
    body: {
      filter: { skill: [characterId], mode: 1 },
      sort: 'win',
      limit: 3,
    },
    throwOnError: true,
  });
  return data.data;
}

// Top 3 pairings (combos of 4 characters) that include the given character, sorted by win rate.
export function useCharacterCombos(characterId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['characters', 'combo', characterId],
    enabled: enabled && !!characterId,
    queryFn: () => fetchCharacterCombos(characterId),
  });
}

async function fetchCharacterSynergy(characterId: number): Promise<ApiSynergyEntry[]> {
  const { data } = await getCharacterSynergy({
    path: { id: characterId },
    body: {
      sort: 'win',
      limit: 10,
    },
    throwOnError: true,
  });
  return data.data;
}

// Top 10 synergy partners for the given character, sorted by win rate.
export function useCharacterSynergies(characterId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['characters', 'synergy', characterId],
    enabled: enabled && !!characterId,
    queryFn: () => fetchCharacterSynergy(characterId),
  });
}

export type ComboFilter = {
  skill?: number[];
  skillAgg?: 'AND' | 'OR';
  hard?: number[];
  role?: number[];
  active?: boolean;
};

// Infinite-scroll hook for the "Top Combos" listing page (SkillCombos).
// Distinct from useCharacterCombos above, which fetches the top 3 pairings for one character.
export function useComboCharactersInfinite(
  mode: 1 | 15,
  filter?: ComboFilter,
  limit: number = 20,
  enabled: boolean = true
) {
  return useInfiniteQuery({
    queryKey: ['characters', 'combo', 'infinite', mode, filter],
    enabled,
    queryFn: async ({ pageParam }) => {
      const { data } = await getComboCharacters({
        body: { cursor: pageParam, limit, filter: { mode, ...filter }, sort: 'win' },
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage?.cursor,
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      combos: Array.from(
        new Map(
          data.pages
            .flatMap((page) => page?.data || [])
            .map((c) => [`${c.skill1}-${c.skill2}-${c.skill3}-${c.skill4}`, c] as const)
        ).values()
      ),
    }),
  });
}
